import React from 'react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConnectionIndicator, ConnectionStatus } from '@/components/common/ConnectionIndicator';
import { Activity, Clock, Terminal } from 'lucide-react';
import { formatTimestamp } from '@/lib/utils';

interface HeaderProps {
  wsStatus: ConnectionStatus;
  wsReconnectAttempt?: number;
  marketStatus?: 'OPEN' | 'CLOSED' | string | null;
  botStatus?: string | null;
  paperMode?: boolean;
  lastCycle?: string | null;
  lastSignal?: string | null;
  isMockMode?: boolean;
  onToggleMockMode?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  wsStatus,
  wsReconnectAttempt = 0,
  marketStatus = 'OPEN',
  botStatus = 'RUNNING',
  paperMode = true,
  lastCycle,
  lastSignal,
  isMockMode,
  onToggleMockMode,
  onRefresh,
  isRefreshing,
}) => {
  return (
    <header className="sticky top-0 z-40 w-full border-b border-[var(--border-default)] bg-[var(--header-bg)] backdrop-blur-md px-4 lg:px-6 py-3">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Left: Branding & Core Mode Badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[var(--accent-muted)] border border-[var(--border-default)] text-[var(--accent-primary)] shadow-[0_0_12px_var(--accent-muted)]">
              <Terminal className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold font-mono tracking-tight text-[var(--text-primary)] text-sm md:text-base">
                  TERMINAL // OPS
                </span>
                {paperMode && (
                  <StatusBadge
                    status="PAPER_MODE"
                    label="PAPER MODE"
                    size="sm"
                  />
                )}
              </div>
              <p className="text-[10px] font-mono text-[var(--text-muted)] hidden sm:block">
                Swing & Delivery Stock Trading Bot
              </p>
            </div>
          </div>

          <div className="h-4 w-px bg-[var(--border-default)] hidden sm:block" />

          {/* Bot & Market Status */}
          <div className="flex items-center gap-2">
            <StatusBadge
              status={botStatus || 'RUNNING'}
              label={`BOT: ${botStatus || 'IDLE'}`}
              size="sm"
            />
            <StatusBadge
              status={marketStatus === 'OPEN' ? 'MARKET_OPEN' : 'MARKET_CLOSED'}
              label={`MARKET: ${marketStatus || 'CLOSED'}`}
              size="sm"
            />
          </div>
        </div>

        {/* Right: Live Telemetry, Connection Indicator & Actions */}
        <div className="flex items-center gap-3 flex-wrap justify-between md:justify-end text-xs font-mono">
          {/* Last cycle & last signal metrics */}
          <div className="hidden lg:flex items-center gap-4 text-[var(--text-muted)] border-r border-[var(--border-default)] pr-4">
            <div className="flex items-center gap-1.5" title="Last background cycle run">
              <Activity className="w-3.5 h-3.5 text-[var(--text-dim)]" />
              <span>Cycle:</span>
              <span className="text-[var(--text-secondary)] tabular-nums">
                {lastCycle ? formatTimestamp(lastCycle, 'time') : 'Waiting'}
              </span>
            </div>
            <div className="flex items-center gap-1.5" title="Last signal processed">
              <Clock className="w-3.5 h-3.5 text-[var(--text-dim)]" />
              <span>Signal:</span>
              <span className="text-[var(--text-secondary)] tabular-nums">
                {lastSignal ? formatTimestamp(lastSignal, 'time') : 'None'}
              </span>
            </div>
          </div>

          {/* WebSocket indicator */}
          <ConnectionIndicator
            status={wsStatus}
            reconnectAttempt={wsReconnectAttempt}
          />

          {/* Demo Data Mode Toggle Button */}
          {onToggleMockMode && (
            <button
              onClick={onToggleMockMode}
              className={`px-2.5 py-1 rounded text-[11px] font-mono border transition-all duration-150 active:scale-95 ${
                isMockMode
                  ? 'bg-amber-950/40 text-amber-300 border-amber-800/60'
                  : 'bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-default)] hover:text-[var(--text-primary)]'
              }`}
              title="Toggle between live backend connection and simulated demo stream"
            >
              {isMockMode ? 'SIMULATION ACTIVE' : 'LIVE BACKEND'}
            </button>
          )}

          {/* Manual refresh button */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-1.5 rounded-md bg-[var(--bg-surface)] border border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-elevated)] active:scale-95 disabled:opacity-50 transition-all duration-150"
              title="Force refresh all REST endpoints"
            >
              <Activity className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
